/**
 * <h2>PDS Logger</h2>
 * <p>
 * There are several components where having the ability to turn on console logging for debugging purposes would be really nice.
 * Especially for component specific logging rather than global logging.  The logger utility allows for just that sort of thing.
 * </p>
 *
 * <h3>Component Specific Loggers</h3>
 * <h4>Set Up</h4>
 * //import pds
 * import pds from 'powerschool-design-system/tool-kit';
 * //require the logger in every file that will use it.
 * let feedLog = pds.logger.requiresLogger('feed');
 * let duLog = pds.logger.requiresLogger('dateUtils');
 *
 * <h4>Use</h4>
 * pds.log.feed('this message is from the feed logger');
 * pds.log.dateUtils('this message is from the dateUtils logger');
 *
 * OR
 *
 * feedLog('this message is from the feed logger');
 * duLog('this message is from the dateUtils logger');
 *
 * <h3>General Logging (console.log() replacement)</h3>
 * Instead of using console.log, you can use pds.log.
 * <h4>Set Up</h4>
 * import pds from 'powerschool-design-system/tool-kit';
 *
 * <h4>Use</h4>
 * pds.log('hello world');
 *
 *
 * <h3>Turn on logging during runtime</h3>
 * The logger persists your decision to localStorage which allows you to turn it on and restart your page in order to get the logging.
 * powerschoolDesignSystemToolkit.logger.setLoggerStatus('feed', true);
 * powerschoolDesignSystemToolkit.logger.setLoggerStatus('dateUtils', true);
 * powerschoolDesignSystemToolkit.logger.setLoggerStatus('log', true);
 *
 *
 * //Turn the grid widget logger on like this:
 * pds.logger.setLoggerStatus('gridWidget', true);
 */
import { pdsLocalStorage } from './pds-local-storage';

export let pdsLogger = {
  //Key: the logger name. Value: true if logs will show up, false if they will not show up.
  loggerStatus: {},
  //Turn on the logger like this: pds.logger.setLoggerStatus('gridWidget', true);
  setLoggerStatus: function (loggerName: string, showMessages: boolean): string {
    this.loggerStatus[loggerName] = showMessages;
    try {
      if (showMessages) {
        pdsLocalStorage.setItem('PDS-LOGGER-' + loggerName, 'true');
      } else {
        pdsLocalStorage.removeItem('PDS-LOGGER-' + loggerName);
      }
    } catch (e) {
      //If this fails, it isn't a big deal.
    }
    return 'Logger "' + loggerName + '" set set to ' + (showMessages ? 'show' : 'hide') + ' messages.\n NOTE: this persists to localStorage';
  },
  /**
   * This gets called at the top of every file that needs to use a component specific logger.  It registers the logger
   * function and also returns it so the developer can use which ever option is more convenient.
   * @param functionName
   * @returns log function.
   */
  requiresLogger: function (functionName: string): (msg: any, ...params: Array<any>) => void {
    //only register things that are here.
    if (this.log[functionName] == null) {
      let loggerFunction = function (msg: any, ...params: Array<any>): void {
        if (this.loggerStatus[functionName]) {
          console.log.apply(console, arguments);
        }
      };
      this.log[functionName] = loggerFunction.bind(this);
      //We only want to turn it off if someone has not said they want it.
      if (!this.loggerStatus.hasOwnProperty(functionName)) {
        this.loggerStatus[functionName] = false;
      }
    }

    return this.log[functionName];
  },
  prependLogger: function(logFunction: (msg: any, ...params: Array<any>) => void, prependString: string): (msg: any, ...params: Array<any>) => void {
    return function(): void {
      let args = Array.from(arguments);
      args.unshift(prependString + ' - ');
      logFunction.apply(logFunction, args);
    };
  },
  /**
   * This log function doubles as two different things.
   * 1) This is the general pds log function.  pds.log('something') and can be turned on and off using setLoggerStatus.
   * 2) This is used as the map object that holds key value pairs where the value is a log function its self.
   * Key: component name, value: log function.
   * Example:  pds.log.gw('this is the grid widget logger function');
   * @param msg Same as the first argument in console.log
   * @param params Same as the rest of the arguments in console.log
   */
  log: function (msg: any, ...params: Array<any>): void {
    if ((<any>this.loggerStatus).log) {
      console.log.apply(console, arguments);
    }
  },
  init(): void {
    //Pull the persisted turned on loggers from localStorage.
    try {
      this.loggerStatus = {};
      let pdsLoggerRegex = /^PDS-LOGGER-/;
      for (let key of Object.keys(localStorage)) {
        if (pdsLoggerRegex.test(key)) {
          if (pdsLocalStorage.getUserPersistedBoolean(key)) {
            this.loggerStatus[key.slice(11)] = true;
          }
        }
      }
    } catch (e) {
      //If this fails, it isn't a big deal.
    }
  }
};

//Bind the function to the logger so it will always execute within that context.
pdsLogger.log = pdsLogger.log.bind(pdsLogger);

pdsLogger.init();
